import { Button, Typography, alpha, useTheme } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useMemo } from "react";

import { ContactCardPreview } from "./ContactCardPreview";
import { renderMessageWithUrls } from "./previewContentUtils";
import {
  ContentArea,
  DynamicIsland,
  MediaBlock,
  MessageBubble,
  MessageText,
  MessageTypeLabel,
  MockupContainer,
  RcsCtaContainer,
  RcsCtaText,
  RcsSuggestionPill,
  RcsSuggestionRow,
  Screen,
  ScreenHeader,
  SenderText,
  Timestamp,
} from "./TextCardPreviewMockup.styles";
import type { TextComposerPreviewContent } from "./useMessagePreview";
import type {
  TextComposerFallbackChannel,
  TextComposerPrimaryChannel,
} from "../channel/channelDerivation";
import {
  isNormalizedAuthoringStateEmpty,
  type TextComposerAuthoringState,
} from "../editor/authoringState";

import { LinkIcon, PhoneIcon, SmsOutlinedIcon } from "@/icons";

const SUGGESTION_PILL_ICONS = {
  REPLY: SmsOutlinedIcon,
  OPEN_URL: LinkIcon,
  DIAL_PHONE: PhoneIcon,
} as const;

function getPreviewForChannel(
  previewByChannel: {
    RCS: TextComposerPreviewContent | null;
    SMS: TextComposerPreviewContent | null;
    MMS: TextComposerPreviewContent | null;
  },
  channel: TextComposerPrimaryChannel | TextComposerFallbackChannel,
) {
  if (channel === "RCS") return previewByChannel.RCS;
  if (channel === "SMS") return previewByChannel.SMS;
  if (channel === "MMS") return previewByChannel.MMS;
  return null;
}

function PreviewErrorBubble({ onRetry }: { onRetry?: () => void }) {
  const theme = useTheme();
  return (
    <MessageBubble
      role="alert"
      sx={{
        backgroundColor: alpha(theme.palette.warning.main, 0.14),
        border: `1px solid ${theme.palette.warning.main}`,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.warning.dark,
          fontWeight: theme.typography.fontWeightMedium,
        }}
      >
        Preview error
      </Typography>
      <Button
        size="small"
        color="warning"
        variant="contained"
        disabled={!onRetry}
        onClick={() => onRetry?.()}
        sx={{ alignSelf: "flex-start", mt: 2 }}
      >
        Retry
      </Button>
    </MessageBubble>
  );
}

interface TextCardPreviewMockupProps {
  activeChannel: TextComposerPrimaryChannel | TextComposerFallbackChannel;
  previewByChannel: {
    RCS: TextComposerPreviewContent | null;
    SMS: TextComposerPreviewContent | null;
    MMS: TextComposerPreviewContent | null;
  };
  mediaNameByUrl: Record<string, string>;
  /** Site `brand_name` from `/accounts/sites/:id/`; shown as the sender label in the bubble. */
  brandName?: string | null;
  /**
   * When set (live composer), the empty-state hint uses authoring emptiness, not preview body text.
   * When omitted (read-only previews), the hint follows preview body text as before.
   */
  authoringState?: TextComposerAuthoringState;
  isPreviewError?: boolean;
  onPreviewRetry?: () => void;
  /**
   * Read-only previews (e.g. frame cards): when the transformed payload has no body to show, render
   * an empty message bubble (sender only, no “Start typing…” placeholder) and omit preview media / RCS CTA.
   */
  readOnlyPreviewIsEmpty?: boolean;
}

export function TextCardPreviewMockup({
  activeChannel,
  previewByChannel,
  mediaNameByUrl,
  brandName,
  authoringState,
  isPreviewError = false,
  onPreviewRetry,
  readOnlyPreviewIsEmpty = false,
}: TextCardPreviewMockupProps) {
  const theme = useTheme();
  const activePreview = useMemo(
    () => getPreviewForChannel(previewByChannel, activeChannel),
    [activeChannel, previewByChannel],
  );

  const isRcs = activeChannel === "RCS";
  const isMms = activeChannel === "MMS";
  const label = isRcs ? "RCS Message" : isMms ? "MMS Message" : "Text Message";

  const transformedBody = activePreview?.body;
  const bodyText =
    transformedBody?.type === "TEXT"
      ? transformedBody.text
      : (transformedBody?.text ?? "");
  const rcsImageUrl =
    isRcs &&
    transformedBody?.type === "IMAGE" &&
    typeof transformedBody.url === "string"
      ? transformedBody.url
      : null;
  const mmsImageUrls =
    isMms &&
    transformedBody?.type === "IMAGE" &&
    typeof transformedBody.url === "string"
      ? [transformedBody.url]
      : [];
  const documentUrl =
    (isRcs || isMms) &&
    transformedBody?.type === "DOCUMENT" &&
    typeof transformedBody.url === "string"
      ? transformedBody.url
      : null;

  const textToDisplay = isRcs ? bodyText : isMms ? bodyText : bodyText;
  const hasMedia =
    rcsImageUrl !== null || mmsImageUrls.length > 0 || documentUrl !== null;
  const fallbackText = "Start typing to see your message...";
  const showEmptyComposerHint = readOnlyPreviewIsEmpty
    ? false
    : hasMedia
      ? false
      : authoringState !== undefined
        ? isNormalizedAuthoringStateEmpty(authoringState)
        : !textToDisplay;
  const messageTextToRender = showEmptyComposerHint
    ? textToDisplay || fallbackText
    : textToDisplay;
  const animationKey = isPreviewError ? "preview-error" : activeChannel;
  const senderLabel = brandName?.trim() ? brandName.trim() : "Your Brand";

  const messageBubbleSx = useMemo(() => {
    const sm = `${theme.shape.borderRadiusSm}px`;
    const lg = `${theme.shape.borderRadiusLg}px`;
    const bubbleFill = "#e5e7eb";
    const rcsSurface = "#ffffff";
    const rcsOutline = "#e5e7eb";

    if (isRcs || isMms) {
      return {
        borderRadius: `${sm} ${lg} ${lg} ${lg}`,
        backgroundColor: isRcs ? rcsSurface : bubbleFill,
        border: isRcs ? `1px solid ${rcsOutline}` : "none",
      };
    }
    return {
      borderBottomLeftRadius: sm,
      borderTopLeftRadius: lg,
      borderTopRightRadius: lg,
      borderBottomRightRadius: lg,
      backgroundColor: bubbleFill,
      border: "none",
    };
  }, [isRcs, isMms, theme]);

  const showPreviewBody = !readOnlyPreviewIsEmpty;

  const suggestions = useMemo(
    () =>
      (authoringState?.suggestions ?? [])
        .map((suggestion) => ({
          ...suggestion,
          text: suggestion.text.trim(),
        }))
        .filter((suggestion) => suggestion.text.length > 0),
    [authoringState?.suggestions],
  );

  return (
    <MockupContainer>
      <DynamicIsland />
      <Screen>
        <ScreenHeader />
        <ContentArea>
          <Timestamp>11:49 AM</Timestamp>
          <MessageTypeLabel>{label}</MessageTypeLabel>

          <AnimatePresence mode="wait">
            <motion.div
              key={animationKey}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: theme.spacing(4),
              }}
            >
              {isPreviewError ? (
                <PreviewErrorBubble onRetry={onPreviewRetry} />
              ) : (
                <MessageBubble sx={messageBubbleSx}>
                  <SenderText>{senderLabel}</SenderText>

                  {showPreviewBody && isRcs && rcsImageUrl ? (
                    <MediaBlock>
                      <img
                        src={rcsImageUrl}
                        alt={mediaNameByUrl[rcsImageUrl] ?? "RCS media"}
                      />
                    </MediaBlock>
                  ) : null}

                  {showPreviewBody && isMms
                    ? mmsImageUrls.map((imageUrl) => (
                        <MediaBlock key={imageUrl}>
                          <img
                            src={imageUrl}
                            alt={mediaNameByUrl[imageUrl] ?? "MMS media"}
                          />
                        </MediaBlock>
                      ))
                    : null}

                  <MessageText
                    sx={
                      readOnlyPreviewIsEmpty ? { minHeight: "20px" } : undefined
                    }
                  >
                    {renderMessageWithUrls(
                      messageTextToRender ?? "",
                      true,
                      false,
                    )}
                  </MessageText>

                  {showPreviewBody &&
                  isRcs &&
                  activePreview?.buttons?.[0]?.text ? (
                    <RcsCtaContainer>
                      <RcsCtaText>{activePreview.buttons[0].text}</RcsCtaText>
                    </RcsCtaContainer>
                  ) : null}
                </MessageBubble>
              )}

              {!isPreviewError && showPreviewBody && documentUrl ? (
                <ContactCardPreview
                  name={mediaNameByUrl[documentUrl] ?? "Contact card"}
                />
              ) : null}

              {!isPreviewError &&
              showPreviewBody &&
              isRcs &&
              suggestions.length > 0 ? (
                <RcsSuggestionRow>
                  {suggestions.map((suggestion, index) => {
                    const Glyph = SUGGESTION_PILL_ICONS[suggestion.type];
                    return (
                      <RcsSuggestionPill key={index}>
                        <Glyph />
                        {suggestion.text}
                      </RcsSuggestionPill>
                    );
                  })}
                </RcsSuggestionRow>
              ) : null}
            </motion.div>
          </AnimatePresence>
        </ContentArea>
      </Screen>
    </MockupContainer>
  );
}
